import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { API_ORIGIN as API_URL } from "../../config/env";
import { getAuthHeaders } from "../../utils/auth";
import { StyledTableCell, StyledTableRow } from "../../utils/constantData";
import { AdminAction, AdminPageLayout, AdminSectionCard, TableRowActions } from "../../components/admin";
import FarmDairyRecordDialog from "./FarmDairyRecordDialog";
import { apiErrorMessage, formatDate, formatMoney, locationText } from "./farmDairyUtils";

function SummaryCard({ label, value, tone }) {
  const tones = {
    income: { background: "#e8f5e9", color: "#166534" },
    expense: { background: "#ffebee", color: "#b91c1c" },
    net: { background: "#e3f2fd", color: "#1d4ed8" },
    neutral: { background: "#f4f7f2", color: "#172112" },
  };
  const style = tones[tone] || tones.neutral;
  return (
    <Box sx={{ background: style.background, borderRadius: "12px", px: 2, py: 1.5, minHeight: 88 }}>
      <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="mt-1 text-xl font-bold" style={{ color: style.color }}>{value}</div>
    </Box>
  );
}

function UserField({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="mt-1 text-sm text-[#172112] break-words">{value || "—"}</div>
    </div>
  );
}

function EntryTypeChip({ entryType }) {
  const income = entryType === "income";
  return (
    <Chip
      size="small"
      label={income ? "Income" : "Expense"}
      color={income ? "success" : "error"}
      variant="outlined"
    />
  );
}

export default function FarmDairyDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadDetail() {
      setLoading(true);
      setError("");
      setPayload(null);
      try {
        const res = await axios.get(`${API_URL}/api/v1/farm-diary/admin/users/${userId}`, {
          headers: getAuthHeaders(),
        });
        if (!cancelled) setPayload(res.data?.data || null);
      } catch (err) {
        if (cancelled) return;
        const message = apiErrorMessage(err, "Failed to load farm diary details");
        setError(message);
        if (err?.response?.status !== 404 && err?.response?.status !== 400) {
          toast.error(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const user = payload?.user;
  const summary = payload?.summary;
  const entries = payload?.entries || [];
  const activities = payload?.activities || [];

  return (
    <AdminPageLayout
      eyebrow="Farm Diary"
      title={user?.name || (loading ? "Farm Diary" : "Farm diary details")}
      description={user ? `${user.mobile || "No mobile"} · ${locationText(user)}` : "Farmer crop entries and farm activities."}
      actions={
        <AdminAction variant="outline" startIcon={<ArrowBack />} onClick={() => navigate("/farm-dairy")}>
          Back to list
        </AdminAction>
      }
    >
      {loading ? (
        <AdminSectionCard>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 6, color: "#78716c" }}>
            <CircularProgress size={22} sx={{ color: "#3f7a2a" }} />
            <span>Loading farm diary details…</span>
          </Box>
        </AdminSectionCard>
      ) : error ? (
        <AdminSectionCard title="Unable to open this farm diary">
          <Box sx={{ py: 2, color: "#c5543d" }}>{error}</Box>
        </AdminSectionCard>
      ) : (
        <div className="grid gap-4">
          <AdminSectionCard title="User details" description="Profile information stored with this farmer.">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <UserField label="Name" value={user.name} />
              <UserField label="Mobile" value={user.mobile} />
              <UserField label="Email" value={user.email} />
              <UserField label="Profession" value={user.profession} />
              <UserField label="Gender" value={user.gender} />
              <UserField label="Language" value={user.languagePreference} />
              <UserField label="Location" value={locationText(user)} />
              <UserField label="Status" value={user.isDeleted ? "Deleted" : "Active"} />
            </div>
          </AdminSectionCard>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <SummaryCard label="Crop income" value={formatMoney(summary?.entryIncome)} tone="income" />
            <SummaryCard label="Crop expense" value={formatMoney(summary?.entryExpense)} tone="expense" />
            <SummaryCard label="Activity income" value={formatMoney(summary?.activityIncome)} tone="income" />
            <SummaryCard label="Activity expense" value={formatMoney(summary?.activityExpense)} tone="expense" />
            <SummaryCard label="Total income" value={formatMoney(summary?.totalIncome)} tone="income" />
            <SummaryCard label="Total expense" value={formatMoney(summary?.totalExpense)} tone="expense" />
            <SummaryCard label="Net" value={formatMoney(summary?.netProfitLoss)} tone="net" />
            <SummaryCard
              label="Records"
              value={`${summary?.entryCount || 0} ${summary?.entryCount === 1 ? "entry" : "entries"} · ${summary?.activityCount || 0} ${summary?.activityCount === 1 ? "activity" : "activities"}`}
              tone="neutral"
            />
          </div>

          <AdminSectionCard
            title={`Crop diary records (${entries.length})`}
            description="Expense and income entries recorded against crops, seasons, and categories."
            className="admin-table-shell"
          >
            <TableContainer component="div">
              <Table sx={{ minWidth: 860 }} aria-label="crop diary entries">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>Crop</StyledTableCell>
                    <StyledTableCell>Season</StyledTableCell>
                    <StyledTableCell>Type</StyledTableCell>
                    <StyledTableCell>Category</StyledTableCell>
                    <StyledTableCell>Amount</StyledTableCell>
                    <StyledTableCell>Cultivation</StyledTableCell>
                    <StyledTableCell>Harvesting</StyledTableCell>
                    <StyledTableCell align="right">Actions</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {entries.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={8} align="center">
                        <Box sx={{ py: 4, color: "#78716c" }}>No crop diary entries for this user.</Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    entries.map((entry) => (
                      <StyledTableRow key={entry.id}>
                        <StyledTableCell>{entry.cropName || "—"}</StyledTableCell>
                        <StyledTableCell>{entry.season || "—"}</StyledTableCell>
                        <StyledTableCell><EntryTypeChip entryType={entry.entryType} /></StyledTableCell>
                        <StyledTableCell>{entry.category || "—"}</StyledTableCell>
                        <StyledTableCell>{formatMoney(entry.amount)}</StyledTableCell>
                        <StyledTableCell>{formatDate(entry.cultivationDate)}</StyledTableCell>
                        <StyledTableCell>{formatDate(entry.harvestingDate)}</StyledTableCell>
                        <StyledTableCell align="right">
                          <TableRowActions
                            rowId={`entry-${entry.id}`}
                            onView={() => setRecord({ kind: "entry", data: entry })}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AdminSectionCard>

          <AdminSectionCard
            title={`Farm activities (${activities.length})`}
            description="Activities such as crops, livestock, or equipment, with their related transactions."
            className="admin-table-shell"
          >
            <TableContainer component="div">
              <Table sx={{ minWidth: 860 }} aria-label="farm activities">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>Activity</StyledTableCell>
                    <StyledTableCell>Category</StyledTableCell>
                    <StyledTableCell>Item</StyledTableCell>
                    <StyledTableCell>Transactions</StyledTableCell>
                    <StyledTableCell>Income</StyledTableCell>
                    <StyledTableCell>Expense</StyledTableCell>
                    <StyledTableCell>Created</StyledTableCell>
                    <StyledTableCell align="right">Actions</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {activities.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={8} align="center">
                        <Box sx={{ py: 4, color: "#78716c" }}>No farm activities for this user.</Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    activities.map((activity) => {
                      const transactions = activity.transactions || [];
                      const income = transactions
                        .filter((item) => item.entryType === "income")
                        .reduce((sum, item) => sum + Number(item.amount || 0), 0);
                      const expense = transactions
                        .filter((item) => item.entryType === "expense")
                        .reduce((sum, item) => sum + Number(item.amount || 0), 0);
                      const itemName = activity.subcategory === "Others"
                        ? activity.customSubcategory || "Others"
                        : activity.subcategory;
                      return (
                        <StyledTableRow key={activity.id}>
                          <StyledTableCell>{activity.displayName || "—"}</StyledTableCell>
                          <StyledTableCell>{activity.category || "—"}</StyledTableCell>
                          <StyledTableCell>{itemName || "—"}</StyledTableCell>
                          <StyledTableCell>{transactions.length}</StyledTableCell>
                          <StyledTableCell sx={{ color: "#166534" }}>{formatMoney(income)}</StyledTableCell>
                          <StyledTableCell sx={{ color: "#b91c1c" }}>{formatMoney(expense)}</StyledTableCell>
                          <StyledTableCell>{formatDate(activity.createdAt)}</StyledTableCell>
                          <StyledTableCell align="right">
                            <TableRowActions
                              rowId={`activity-${activity.id}`}
                              onView={() => setRecord({ kind: "activity", data: activity })}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AdminSectionCard>
        </div>
      )}

      <FarmDairyRecordDialog
        open={Boolean(record)}
        record={record}
        onClose={() => setRecord(null)}
      />
    </AdminPageLayout>
  );
}
